import React, { useEffect, useState } from 'react';
import {
  X, RefreshCw, TrendingUp, TrendingDown, Minus,
  Truck, Clock, Star, Target, Wallet, Activity, MapPin, Loader2, Gift
} from 'lucide-react';
import { apiService } from '../../services/api.service';
import { API_ENDPOINTS } from '../../config/environment';
import { formatTL } from '../../utils/money';
import './MemberStatisticsModal.css';

const fmt = (v) => {
  if (v == null) return '-';
  return Number(v).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const fmtInt = (v) => (v != null ? Number(v).toLocaleString('tr-TR') : '-');

const ChangeIndicator = ({ value }) => {
  if (value == null || value === 0) return <span className="ms-change neutral"><Minus size={12} /> %0</span>;
  if (value > 0) return <span className="ms-change up"><TrendingUp size={12} /> %{fmt(value)}</span>;
  return <span className="ms-change down"><TrendingDown size={12} /> %{fmt(Math.abs(value))}</span>;
};

const MemberStatisticsModal = ({ member, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await apiService.get(API_ENDPOINTS.FLEET_PANEL.MEMBER_STATISTICS(member.id));
      if (res.success) {
        setData(res.data);
      } else {
        setError(res.error || 'Veriler alınamadı');
      }
    } catch {
      setError('Beklenmeyen bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [member.id]);

  const profile = data?.profile;
  const earnings = data?.earnings;
  const deliveryStats = data?.deliveryStats;
  const workingHours = data?.workingHours;
  const performance = data?.performance;
  const dailyGoals = data?.dailyGoals;
  const pricing = data?.pricing;
  const pendingPayments = data?.pendingPayments;

  const renderOverview = () => (
    <div className="ms-grid">
      {profile && (
        <div className="ms-profile-card">
          <div className="ms-profile-info">
            <h3>{profile.fullName}</h3>
            <div className="ms-profile-badges">
              <span className={`ms-badge ${profile.isActive ? 'active' : 'inactive'}`}>
                {profile.isActive ? 'Aktif' : 'Pasif'}
              </span>
              <span className={`ms-badge ${profile.isBusy ? 'busy' : 'available'}`}>
                {profile.isBusy ? 'Meşgul' : 'Müsait'}
              </span>
            </div>
          </div>
          <div className="ms-profile-stats">
            <div className="ms-profile-stat"><Star size={14} /><span>{fmt(profile.rating)}</span></div>
            <div className="ms-profile-stat"><Truck size={14} /><span>{fmtInt(profile.totalCompletedDeliveries)} teslimat</span></div>
            {profile.vehiclePlate && <div className="ms-profile-stat"><span>{profile.vehiclePlate}</span></div>}
          </div>
        </div>
      )}

      <div className="ms-kpi-row">
        <div className="ms-kpi earnings">
          <div className="ms-kpi-icon"><Wallet size={18} /></div>
          <div className="ms-kpi-content">
            <span className="ms-kpi-label">Bugün</span>
            <span className="ms-kpi-value">{formatTL(earnings?.daily?.totalEarnings)}</span>
            <ChangeIndicator value={earnings?.daily?.changePercent} />
          </div>
        </div>
        <div className="ms-kpi deliveries">
          <div className="ms-kpi-icon"><Truck size={18} /></div>
          <div className="ms-kpi-content">
            <span className="ms-kpi-label">Teslimat</span>
            <span className="ms-kpi-value">{fmtInt(deliveryStats?.daily?.successfulDeliveries)}</span>
            <span className="ms-kpi-sub">{fmtInt(deliveryStats?.daily?.totalDeliveries)} toplam</span>
          </div>
        </div>
        <div className="ms-kpi hours">
          <div className="ms-kpi-icon"><Clock size={18} /></div>
          <div className="ms-kpi-content">
            <span className="ms-kpi-label">Çalışma</span>
            <span className="ms-kpi-value">{workingHours?.daily?.totalHoursText || '-'}</span>
          </div>
        </div>
        <div className="ms-kpi performance">
          <div className="ms-kpi-icon"><Activity size={18} /></div>
          <div className="ms-kpi-content">
            <span className="ms-kpi-label">Performans</span>
            <span className="ms-kpi-value">{fmtInt(performance?.monthlyPerformanceScore)}</span>
            <span className="ms-kpi-sub">%{fmt(performance?.monthlySuccessRate)} başarı</span>
          </div>
        </div>
      </div>

      {dailyGoals && (
        <div className="ms-goals-card">
          <h4><Target size={16} /> Günlük Hedef</h4>
          <div className="ms-goal-bars">
            <div className="ms-goal-item">
              <div className="ms-goal-header">
                <span>Teslimat ({dailyGoals.completedDeliveries}/{dailyGoals.targetDeliveries})</span>
                <span>%{fmt(dailyGoals.progressPercent)}</span>
              </div>
              <div className="ms-goal-bar"><div className="ms-goal-fill delivery-fill" style={{ width: `${Math.min(dailyGoals.progressPercent, 100)}%` }} /></div>
            </div>
            <div className="ms-goal-item">
              <div className="ms-goal-header">
                <span>Kazanç ({formatTL(dailyGoals.currentEarnings)}/{formatTL(dailyGoals.targetEarnings)})</span>
                <span>%{fmt(dailyGoals.earningsProgressPercent)}</span>
              </div>
              <div className="ms-goal-bar"><div className="ms-goal-fill earnings-fill" style={{ width: `${Math.min(dailyGoals.earningsProgressPercent, 100)}%` }} /></div>
            </div>
          </div>
        </div>
      )}

      {pricing && pricing.isActive && (
        <div className="ms-pricing-card">
          <h4><MapPin size={16} /> Fiyatlandırma</h4>
          <div className="ms-pricing-values">
            <div className="ms-pricing-item">
              <span className="ms-pricing-label">Sabit Ücret</span>
              <span className="ms-pricing-val">{formatTL(pricing.baseFee)}</span>
            </div>
            <div className="ms-pricing-item">
              <span className="ms-pricing-label">Km Başı</span>
              <span className="ms-pricing-val">{formatTL(pricing.costPerKm)}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderEarnings = () => (
    <div className="ms-grid">
      <div className="ms-period-cards">
        {[
          { label: 'Günlük', data: earnings?.daily },
          { label: 'Haftalık', data: earnings?.weekly },
          { label: 'Aylık', data: earnings?.monthly }
        ].map(({ label, data: p }) => (
          <div className="ms-period-card" key={label}>
            <div className="ms-period-header"><h4>{label}</h4><ChangeIndicator value={p?.changePercent} /></div>
            <div className="ms-period-value">{formatTL(p?.totalEarnings)}</div>
            <div className="ms-period-details">
              <span>{fmtInt(p?.deliveryCount)} teslimat</span>
              <span>{formatTL(p?.avgEarningPerDelivery)}/teslimat</span>
              <span>{formatTL(p?.avgEarningPerHour)}/saat</span>
            </div>
            {Number(p?.packageBonus) > 0 && (
              <div className="ms-period-bonus">
                <span className="ms-period-bonus-row"><Gift size={12} /> Paket Bonusu</span>
                <strong>{formatTL(p.packageBonus)}</strong>
              </div>
            )}
            {Number(p?.packageBonus) > 0 && (
              <div className="ms-period-total">
                <span>Toplam</span>
                <strong>{formatTL(Number(p?.totalEarnings || 0) + Number(p.packageBonus))}</strong>
              </div>
            )}
          </div>
        ))}
      </div>

      {earnings?.last7Days?.length > 0 && (
        <div className="ms-breakdown-card">
          <h4>Son 7 Gün</h4>
          <div className="ms-breakdown-list">
            {earnings.last7Days.map((d, i) => (
              <div className={`ms-breakdown-row ${d.isToday ? 'today' : ''}`} key={i}>
                <span className="ms-breakdown-day">{d.dayName}</span>
                <span className="ms-breakdown-value">{formatTL(d.earnings)}</span>
                <span className="ms-breakdown-sub">{fmtInt(d.deliveryCount)} teslimat</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {pendingPayments && pendingPayments.totalPendingAmount > 0 && (
        <div className="ms-pending-card">
          <h4><Wallet size={16} /> Bekleyen Hakediş</h4>
          <div className="ms-pending-total">{formatTL(pendingPayments.totalPendingAmount)}</div>
          <div className="ms-pending-details">
            <span>Onay bekleyen: {fmtInt(pendingPayments.pendingApprovalCount)}</span>
            <span>Fatura bekleyen: {fmtInt(pendingPayments.waitingForInvoiceCount)}</span>
          </div>
        </div>
      )}
    </div>
  );

  const renderDeliveries = () => (
    <div className="ms-grid">
      <div className="ms-period-cards">
        {[
          { label: 'Günlük', data: deliveryStats?.daily },
          { label: 'Haftalık', data: deliveryStats?.weekly },
          { label: 'Aylık', data: deliveryStats?.monthly }
        ].map(({ label, data: p }) => (
          <div className="ms-period-card" key={label}>
            <h4>{label}</h4>
            <div className="ms-period-value">{fmtInt(p?.successfulDeliveries)} / {fmtInt(p?.totalDeliveries)}</div>
            <div className="ms-period-details">
              <span>%{fmt(p?.successRate)} başarı</span>
              <span>{fmtInt(p?.rejectedDeliveries)} ret</span>
              <span>{fmtInt(p?.avgDeliveryTimeMinutes)} dk ort.</span>
              <span>{fmt(p?.totalDistanceKm)} km</span>
            </div>
          </div>
        ))}
      </div>

      {deliveryStats?.overall && (
        <div className="ms-overall-card">
          <h4>Genel</h4>
          <div className="ms-overall-stats">
            <div className="ms-overall-item">
              <span className="ms-overall-label">Toplam Teslimat</span>
              <span className="ms-overall-value">{fmtInt(deliveryStats.overall.totalCompletedDeliveries)}</span>
            </div>
            <div className="ms-overall-item">
              <span className="ms-overall-label">Kabul Oranı</span>
              <span className="ms-overall-value">%{fmt(deliveryStats.overall.overallAcceptanceRate)}</span>
            </div>
            <div className="ms-overall-item">
              <span className="ms-overall-label">Başarı Oranı</span>
              <span className="ms-overall-value">%{fmt(deliveryStats.overall.overallSuccessRate)}</span>
            </div>
            <div className="ms-overall-item">
              <span className="ms-overall-label">Toplam Mesafe</span>
              <span className="ms-overall-value">{fmt(deliveryStats.overall.totalDistanceKm)} km</span>
            </div>
          </div>
        </div>
      )}

      {deliveryStats?.last7DaysDeliveries?.length > 0 && (
        <div className="ms-breakdown-card">
          <h4>Son 7 Gün</h4>
          <div className="ms-breakdown-list">
            {deliveryStats.last7DaysDeliveries.map((d, i) => (
              <div className={`ms-breakdown-row ${d.isToday ? 'today' : ''}`} key={i}>
                <span className="ms-breakdown-day">{d.dayName}</span>
                <span className="ms-breakdown-value">{fmtInt(d.successCount)}/{fmtInt(d.deliveryCount)}</span>
                <span className="ms-breakdown-sub">{fmt(d.distanceKm)} km</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const renderWorkingHours = () => (
    <div className="ms-grid">
      <div className="ms-period-cards">
        {[
          { label: 'Günlük', data: workingHours?.daily },
          { label: 'Haftalık', data: workingHours?.weekly },
          { label: 'Aylık', data: workingHours?.monthly }
        ].map(({ label, data: p }) => (
          <div className="ms-period-card" key={label}>
            <div className="ms-period-header"><h4>{label}</h4><ChangeIndicator value={p?.hoursChangePercent} /></div>
            <div className="ms-period-value">{p?.totalHoursText || '-'}</div>
            <div className="ms-period-details">
              <span>{fmtInt(p?.activeDays)} aktif gün</span>
            </div>
          </div>
        ))}
      </div>

      {workingHours?.currentShift && (
        <div className="ms-shift-card">
          <h4><Activity size={16} /> Aktif Vardiya</h4>
          <div className="ms-shift-info">
            <span className="ms-shift-duration">{workingHours.currentShift.durationText}</span>
            <span>{fmtInt(workingHours.currentShift.deliveriesDuringShift)} teslimat</span>
            <span>{formatTL(workingHours.currentShift.earningsDuringShift)}</span>
          </div>
        </div>
      )}

      {workingHours?.last7DaysHours?.length > 0 && (
        <div className="ms-breakdown-card">
          <h4>Son 7 Gün</h4>
          <div className="ms-breakdown-list">
            {workingHours.last7DaysHours.map((d, i) => (
              <div className={`ms-breakdown-row ${d.isToday ? 'today' : ''}`} key={i}>
                <span className="ms-breakdown-day">{d.dayName}</span>
                <span className="ms-breakdown-value">{d.workingHoursText || '-'}</span>
                <span className="ms-breakdown-sub">{fmtInt(d.deliveryCount)} teslimat</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const tabs = [
    { id: 'overview', label: 'Genel' },
    { id: 'earnings', label: 'Kazanç' },
    { id: 'deliveries', label: 'Teslimat' },
    { id: 'hours', label: 'Çalışma' }
  ];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-wide ms-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h2>Kurye İstatistikleri</h2>
            {member.courierName && <p className="ms-subtitle">{member.courierName}</p>}
          </div>
          <div className="ms-header-actions">
            <button className="btn btn-secondary btn-icon" onClick={load} disabled={loading}>
              <RefreshCw size={16} className={loading ? 'spinning' : ''} />
            </button>
            <button className="modal-close" onClick={onClose}><X size={20} /></button>
          </div>
        </div>

        {data && (
          <div className="ms-tabs">
            {tabs.map(tab => (
              <button
                key={tab.id}
                className={`ms-tab ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        )}

        <div className="ms-body">
          {loading && <div className="page-loading" style={{ padding: '40px 0' }}><Loader2 className="spinning" size={28} /><p>Yükleniyor...</p></div>}
          {error && <div className="ms-error">{error}</div>}
          {data && activeTab === 'overview' && renderOverview()}
          {data && activeTab === 'earnings' && renderEarnings()}
          {data && activeTab === 'deliveries' && renderDeliveries()}
          {data && activeTab === 'hours' && renderWorkingHours()}
        </div>
      </div>
    </div>
  );
};

export default MemberStatisticsModal;
